import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import type { Ecom } from 'models/Ecom';

import { EcomResponse_MarketDrivenShipping } from '../../../pb/responses/v1/ecom_response_pb';

import EcomService from 'services/EcomService';

import { RootState } from 'redux/store';

import { makeRequestWithRetry } from 'utils/axios';

export interface EcomsState {
    selected: Ecom | null;
    id: number | null;
    fetching: boolean;
}

const initialState: EcomsState = {
    selected: null,
    id: null,
    fetching: false,
};

/** Retries after the first attempt — 5 attempts in total. */
const ECOM_NOT_FOUND_RETRIES = 4;
const ECOM_RETRY_DELAY_MS = 2000;

/**
 * `/api/v1/ecom` returns the ecom of the current session — there is no list to select from any more.
 *
 * A 404 is retried every 2 seconds, up to 5 attempts in total: right after install the ecom can still
 * be being created on the backend, so a "not found" there is usually just early. Any other error, or a
 * 404 on the last attempt, rejects straight away.
 */
export const ecomThunk = createAsyncThunk<Ecom>('ecom', async (_, thunkAPI) => {
    try {
        return await makeRequestWithRetry(
            () => EcomService.getEcom(),
            ECOM_NOT_FOUND_RETRIES,
            ECOM_RETRY_DELAY_MS,
            404
        );
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const currentEcomSelector = createSelector(
    (state: RootState) => state.ecoms,
    (ecoms) => ecoms.selected
);

/**
 * Where this ecom stands on market driven shipping — `undefined` until the ecom is loaded. Callers
 * compare against `EcomResponse_MarketDrivenShipping` themselves, since the stages differ in what
 * they gate (e.g. `ACTIVE` means Shopify markets own the shipping zones and rates).
 */
export const marketDrivenShippingSelector = createSelector(
    currentEcomSelector,
    (ecom): EcomResponse_MarketDrivenShipping | undefined => ecom?.marketDrivenShipping
);

export const ecomsSlice = createSlice({
    name: 'ecoms',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(ecomThunk.pending, (state) => {
            state.fetching = true;
        });
        builder.addCase(ecomThunk.fulfilled, (state, action) => {
            state.selected = action.payload;
            state.id = action.payload?.id ?? null;
            state.fetching = false;
        });
        builder.addCase(ecomThunk.rejected, (state) => {
            state.fetching = false;
        });
    },
});

export default ecomsSlice.reducer;
